import { SearchOutlined } from "@ant-design/icons";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Checkbox, Input, Space } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import { GridLoadingIndicator } from "components/widgets";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";

const ACTION_COLUMN_ID = "action";

function ActionCell(params: any) {
  const { data, context } = params;
  const { linkedFeatures, disableCheckbox } = context;
  const isChecked = linkedFeatures.some(
    (feature: any) => feature.id === data.id
  );
  const toggle = () => {
    if (disableCheckbox) return;
    context.onCheckBoxAction({ checked: !isChecked, feature: data });
  };

  return (
    <div className="flex items-center gap-2 h-full">
      <Checkbox
        checked={isChecked}
        disabled={disableCheckbox}
        onChange={toggle}
        data-testid="featuresgrid-checkbox-action"
      />
      <span
        className={`features-grid-action-label${
          isChecked ? " features-grid-action-label--linked" : ""
        }`}
      >
        {isChecked ? "Linked" : "Link"}
      </span>
    </div>
  );
}

function ManageGridSearch(
  props: Readonly<{
    gridRef: React.RefObject<AgGridReact<any>>;
    placeholder: string;
  }>
) {
  const { gridRef, placeholder } = props;
  const [text, setText] = useState("");

  const applyFilter = useCallback(
    (value: string) => {
      gridRef.current?.api?.setGridOption("quickFilterText", value);
    },
    [gridRef]
  );
  const debouncedFilter = useMemo(
    () => debounce(applyFilter, 300),
    [applyFilter]
  );

  useEffect(() => () => debouncedFilter.cancel(), [debouncedFilter]);

  return (
    <Space.Compact className="feature-links-manage-search w-full">
      <Input
        size="large"
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          debouncedFilter(e.target.value);
        }}
        onPressEnter={() => applyFilter(text)}
        data-testid="featuresgrid-manage-search-input"
      />
      <Button
        size="large"
        icon={<SearchOutlined />}
        onClick={() => applyFilter(text)}
        aria-label="Search"
        data-testid="featuresgrid-manage-search-button"
      />
    </Space.Compact>
  );
}

function CellWithCheckbox(params: any) {
  const { data, context, value } = params;
  const { linkedFeatures, disableCheckbox } = context;
  const isChecked = linkedFeatures.some(
    (feature: any) => feature.id === data.id
  );
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        checked={isChecked}
        disabled={disableCheckbox}
        onChange={() => {
          if (disableCheckbox) return;
          context.onCheckBoxAction({
            checked: !isChecked,
            feature: data
          });
        }}
        data-testid="featuresgrid-checkbox-number"
      />
      <span>{value}</span>
    </div>
  );
}

function FeaturesGrid({
  featuresList,
  onCheckBoxAction,
  linkedFeatures,
  isLoadingFeaturesList,
  disableCheckbox = false,
  headerLabel = "Link Bid Packages",
  emptyLabel = "No Bid Packages found",
  checkboxColumnField = "number",
  variant = "default",
  searchPlaceholder = "Search"
}: {
  featuresList: any;
  onCheckBoxAction: (data: any) => void;
  linkedFeatures: any[];
  isLoadingFeaturesList: boolean;
  disableCheckbox?: boolean;
  headerLabel?: string;
  emptyLabel?: string;
  checkboxColumnField?: "number" | "auto_inc_value";
  variant?: "default" | "manage";
  searchPlaceholder?: string;
}) {
  const isManage = variant === "manage";
  const gridRef = useRef<AgGridReact<any>>(null);

  const [isGridReady, setGridReady] = useState(false);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: false,
      sortable: false,
      resizable: true,
      editable: false,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (isManage) {
      return [
        {
          headerName: "Action",
          colId: ACTION_COLUMN_ID,
          width: 130,
          suppressSizeToFit: true,
          cellRenderer: ActionCell
        },
        {
          headerName: checkboxColumnField === "number" ? "Number" : "ID",
          field: checkboxColumnField,
          width: 110,
          suppressSizeToFit: true,
          sortable: true,
          sort: "asc"
        },
        {
          headerName: "Name",
          field: "title",
          sortable: true
        }
      ];
    }
    return [
      {
        headerName: checkboxColumnField === "number" ? "Number" : "ID",
        field: checkboxColumnField,
        width: 250,
        suppressSizeToFit: true,
        cellRenderer: CellWithCheckbox,
        sort: "asc"
      },
      {
        headerName: "Name",
        field: "title"
      }
    ];
  }, [checkboxColumnField, isManage]);

  const onFirstDataRendered = useCallback(() => {
    gridRef?.current?.api?.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    if (isGridReady && gridRef.current && linkedFeatures) {
      gridRef.current.api.refreshCells({
        columns: [isManage ? ACTION_COLUMN_ID : checkboxColumnField],
        force: true
      });
    }
  }, [
    checkboxColumnField,
    disableCheckbox,
    isGridReady,
    isManage,
    linkedFeatures
  ]);

  return (
    <div className="h-full w-full overflow-hidden flex flex-col">
      {isManage ? (
        <div className="pb-3">
          <ManageGridSearch gridRef={gridRef} placeholder={searchPlaceholder} />
        </div>
      ) : (
        <div className="py-2 uppercase text-xs flex items-center justify-between">
          <div>{headerLabel}</div>
          <CiqAgSearch
            key="feature_activity_linking_search"
            gridRef={gridRef}
            placeholder="Search"
          />
        </div>
      )}
      <div className="ag-theme-alpine flex-1 relative">
        {!featuresList && !isLoadingFeaturesList && (
          <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center text-ciq-gray-950 text-opacity-80 text-xs italic">
            {emptyLabel}
          </div>
        )}
        {!featuresList && isLoadingFeaturesList ? (
          <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center">
            <GridLoadingIndicator />
          </div>
        ) : null}
        <AgGridReact<any>
          ref={gridRef}
          rowData={featuresList}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={isManage ? 52 : 40}
          rowHeight={isManage ? 46 : undefined}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          suppressRowDrag
          suppressMovableColumns
          suppressCellFocus
          suppressDragLeaveHidesColumns
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
          loading={false}
          suppressNoRowsOverlay
          onFirstDataRendered={onFirstDataRendered}
          context={{ onCheckBoxAction, linkedFeatures, disableCheckbox }}
        />
      </div>
    </div>
  );
}

export default FeaturesGrid;
